import { toast } from '@/components/ui/sonner';
import { trackLeadConversion } from '@/lib/analytics';

const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
const ENDPOINT =
  process.env.REACT_APP_SUBMIT_LEAD_URL ||
  (supabaseUrl ? `${supabaseUrl}/functions/v1/submit-lead` : '');

const APIKEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

export const ERROR_MESSAGES = {
  invalid_phone:
    'Please enter a valid phone number with country code (e.g. +91 98765 43210).',
  invalid_email: 'Please enter a valid email address.',
  missing_name: 'Please enter your name.',
  missing_message: 'Please add a short message.',
  missing_pet_name: "Please enter your pet's name.",
  missing_breed: "Please enter your pet's breed.",
  invalid_dates:
    'Please pick a valid check-in and check-out date (check-in from today, stays up to 60 nights).',
  invalid_reason: 'Please choose a valid reason.',
  reason_not_allowed: "That reason isn't supported.",
  service_not_allowed: "That service isn't supported.",
  field_too_long: 'One of the fields is too long.',
  invalid_payload: 'Some fields look wrong — please check and try again.',
  invalid_json: 'Something went wrong sending your request.',
  payload_too_large: 'Your message is too long.',
  invalid_source: 'Form misconfigured. Please reach us on WhatsApp.',
  method_not_allowed: 'Something went wrong. Please try again.',
  origin_not_allowed: "This site isn't authorised to send messages.",
  insert_failed: "We couldn't save your message. Please try again.",
  internal_error: 'Something went wrong on our side. Please try again.',
  rate_limited:
    "You've sent a few messages already — please try again in a little while.",
  network_error:
    'Network error — please check your connection and try again.',
  missing_apikey:
    "The form isn't fully configured. Please reach us on WhatsApp.",
  missing_endpoint:
    "The form isn't fully configured. Please reach us on WhatsApp.",
  consent_required: 'Please agree to the Privacy Policy before sending.',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+[1-9]\d{7,14}$/;

export const normalizePhone = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).replace(/[\s\-()._]/g, '');
  if (trimmed.startsWith('+')) return trimmed;
  if (/^91\d{10}$/.test(trimmed)) return `+${trimmed}`;
  if (/^[6-9]\d{9}$/.test(trimmed)) return `+91${trimmed}`;
  return trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
};

const clamp = (value, max) => String(value || '').trim().slice(0, max);

export const submitLead = async (payload) => {
  if (!APIKEY) {
    toast.error(ERROR_MESSAGES.missing_apikey);
    return { success: false, error: 'missing_apikey' };
  }
  if (!ENDPOINT) {
    toast.error(ERROR_MESSAGES.missing_endpoint);
    return { success: false, error: 'missing_endpoint' };
  }
  if (!payload?.consent) {
    toast.error(ERROR_MESSAGES.consent_required);
    return { success: false, error: 'consent_required' };
  }

  const name = clamp(payload.name, 120);
  const email = clamp(payload.email, 254);
  const phone = normalizePhone(payload.phone);
  const message = clamp(payload.message, 2000);

  if (!name) {
    toast.error(ERROR_MESSAGES.missing_name);
    return { success: false, error: 'missing_name' };
  }
  if (!EMAIL_RE.test(email)) {
    toast.error(ERROR_MESSAGES.invalid_email);
    return { success: false, error: 'invalid_email' };
  }
  if (!PHONE_RE.test(phone)) {
    toast.error(ERROR_MESSAGES.invalid_phone);
    return { success: false, error: 'invalid_phone' };
  }
  if (!message) {
    toast.error(ERROR_MESSAGES.missing_message);
    return { success: false, error: 'missing_message' };
  }

  // Strip client-only consent flag; edge function expects known lead fields only.
  const { consent: _consent, ...rest } = payload;
  const body = {
    ...rest,
    name,
    email,
    phone,
    message,
  };
  void _consent;

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: APIKEY,
      },
      body: JSON.stringify(body),
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      toast.error(ERROR_MESSAGES.internal_error);
      return { success: false, error: 'internal_error' };
    }

    if (data?.success) {
      trackLeadConversion(payload?.source);
      toast.success("Thanks! We've received your message — we'll be in touch shortly.");
      return data;
    }

    const code = data?.error || 'internal_error';
    toast.error(ERROR_MESSAGES[code] || 'Something went wrong. Please try again.');
    return data || { success: false, error: code };
  } catch {
    toast.error(ERROR_MESSAGES.network_error);
    return { success: false, error: 'network_error' };
  }
};
